import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import PageContainerHeader from "../../molecules/PageContainerHeader/PageContainerHeader";
import {
  createMachineUserAPI,
  deleteMachineUserAPI,
  getAttendanceCalendarAPI,
  getAttendanceConfigAPI,
  getAttendanceDailyAnalyticsAPI,
  getAttendanceLogsAPI,
  getMachineUsersAPI,
  getAttendanceWebhookStatusAPI,
  syncDashboardUserToMachineAPI,
  syncAttendanceAPI,
  updateMachineUserAPI,
  updateAttendanceConfigAPI,
} from "../../../Redux/Apis/attendanceApi";
import {
  canAttendanceOverview,
  canAttendanceHistory,
  canAttendanceTeam,
  canAttendanceIntegration,
  canAttendanceIntegrationMutate,
  canAttendanceReadAllEmployees,
  canAttendanceMachineLogs,
  canAttendanceIntegrationConfigRead,
  canAttendanceMachineUsersRead,
  hasAdminAccess,
} from "../../../app/utils/common/Functions";
import {
  classifyPunchForDisplay,
  formatPunchDisplay,
} from "../../../app/utils/common/attendancePunchDisplay";
import EmployeeSelect from "../../atoms/EmployeeSelect/EmployeeSelect";
import AttendanceOverviewPanel from "./AttendanceOverviewPanel";
import AttendanceHistoryPanel from "./AttendanceHistoryPanel";
import AttendanceTeamPanel from "./AttendanceTeamPanel";
import "./AttendancePage.scss";

const localizer = momentLocalizer(moment);

const defaultConfig = {
  baseUrl: "",
  attendancePath: "/iclock/cdata.aspx",
  apiKey: "",
  bearerToken: "",
  siteId: "",
  timeoutMs: 10000,
  webhookSecret: "",
  integrationMode: "ADMS_PUSH",
  pullEnabled: false,
  machineUsersListPath: "",
  machineUsersCreatePath: "",
  machineUsersUpdatePath: "",
  machineUsersDeletePath: "",
};

const AttendancePage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth.auth);
  const {
    calendar,
    loading,
    syncing,
    config,
    savingConfig,
    lastSyncAt,
    webhookStatus,
    logs,
    logsLoading,
    logsPagination,
    machineUsers,
    machineUsersLoading,
    machineUsersMutating,
    analyticsOverview,
    analyticsHistory,
    analyticsLoading,
  } = useSelector((state) => state.attendance);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDayRows, setSelectedDayRows] = useState([]);
  const [configForm, setConfigForm] = useState(defaultConfig);
  const [logsPage, setLogsPage] = useState(1);
  const [logsFilters, setLogsFilters] = useState({
    employeeCode: "",
    source: "",
  });
  const [adminDateRange, setAdminDateRange] = useState({
    from: moment().startOf("month").format("YYYY-MM-DD"),
    to: moment().endOf("month").format("YYYY-MM-DD"),
  });
  const [machineUserForm, setMachineUserForm] = useState({
    employeeCode: "",
    name: "",
    password: "",
    role: "employee",
    cardNo: "",
  });
  const [selectedDashboardUserId, setSelectedDashboardUserId] = useState(null);
  const [nonAdminDateRange, setNonAdminDateRange] = useState({
    from: moment().format("YYYY-MM-DD"),
    to: moment().format("YYYY-MM-DD"),
  });
  const [overviewMonth, setOverviewMonth] = useState(() => moment());
  const [historyFrom, setHistoryFrom] = useState(() => moment().subtract(29, "day").format("YYYY-MM-DD"));
  const [historyTo, setHistoryTo] = useState(() => moment().format("YYYY-MM-DD"));
  const [historySearch, setHistorySearch] = useState("");

  const pathTab = useMemo(() => {
    const p = location.pathname || "";
    if (p.includes("/attendance/history")) return "history";
    if (p.includes("/attendance/team")) return "team";
    if (p.includes("/attendance/integration")) return "integration";
    return "overview";
  }, [location.pathname]);
  const timeZoneLabel = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "Local Time", []);
  const isAdminRole = hasAdminAccess(user);
  const canOverview = canAttendanceOverview();
  const canHistory = canAttendanceHistory();
  const canTeam = canAttendanceTeam();
  const canIntegration = canAttendanceIntegration();
  const canIntegrationMutate = canAttendanceIntegrationMutate();
  const canReadAllEmployees = canAttendanceReadAllEmployees();
  const canMachineLogs = canAttendanceMachineLogs();
  const canIntegrationConfigRead = canAttendanceIntegrationConfigRead();
  const canMachineUsersRead = canAttendanceMachineUsersRead();
  const useWideLogDateRange = isAdminRole || canReadAllEmployees;
  const showIntegrationDetailColumns = isAdminRole || canReadAllEmployees;
  /** HR (read users) and admins see per-employee history with In/Out + late; self-service users see only their own simplified column. */
  const showStaffHistoryView = isAdminRole || canReadAllEmployees;
  const machineUsersConfigured = Boolean(String(config?.machineUsersListPath || "").trim());
  const attendanceEmployeeExtraOptions = useMemo(
    () => (canReadAllEmployees ? [{ label: "All employees", value: "all" }] : []),
    [canReadAllEmployees]
  );

  const logsSorted = useMemo(
    () => [...(logs || [])].sort((a, b) => new Date(b.punchTime) - new Date(a.punchTime)),
    [logs]
  );

  useEffect(() => {
    if (!user) return;
    if (canReadAllEmployees) {
      setSelectedEmployee((s) => (s == null ? "all" : s));
    } else {
      setSelectedEmployee((s) => (s == null ? user.id || "me" : s));
    }
  }, [user?.id, canReadAllEmployees]);

  const employeeSelectValue =
    selectedEmployee ?? (canReadAllEmployees ? "all" : user?.id ?? "me");

  const resolveLogsApiUserId = (employeeOverride) => {
    if (!canReadAllEmployees) return "me";
    const s =
      employeeOverride !== undefined && employeeOverride !== null
        ? employeeOverride
        : selectedEmployee ?? "all";
    if (s === "all") return "all";
    if (s === "me") return "me";
    return s;
  };

  const resolveAnalyticsUserId = () => {
    if (!user?.id) return undefined;
    if (!canReadAllEmployees) return user.id;
    const s = selectedEmployee ?? "all";
    if (s === "all" || s == null) return user.id;
    if (s === "me") return user.id;
    return s;
  };

  useEffect(() => {
    if (!canOverview && pathTab === "team") navigate("/attendance", { replace: true });
  }, [canOverview, pathTab, navigate]);

  useEffect(() => {
    if (!canIntegration && pathTab === "integration") navigate("/attendance", { replace: true });
  }, [canIntegration, pathTab, navigate]);

  useEffect(() => {
    if (pathTab !== "overview" || canTeam || canOverview) return;
    if (canHistory) navigate("/attendance/history", { replace: true });
    else if (canIntegration) navigate("/attendance/integration", { replace: true });
  }, [pathTab, canOverview, canHistory, canTeam, canIntegration, navigate]);

  useEffect(() => {
    if (pathTab !== "history" || canHistory) return;
    if (canTeam || canOverview) navigate("/attendance", { replace: true });
    else if (canIntegration) navigate("/attendance/integration", { replace: true });
  }, [pathTab, canHistory, canOverview, canTeam, canIntegration, navigate]);

  useEffect(() => {
    if (!canOverview || !user?.id) return;
    const onPersonalOverviewPanel =
      !canTeam && (pathTab === "team" || pathTab === "overview");
    if (!onPersonalOverviewPanel) return;
    const todayStr = moment().format("YYYY-MM-DD");
    let from = overviewMonth.clone().startOf("month").format("YYYY-MM-DD");
    let to = overviewMonth.clone().endOf("month").format("YYYY-MM-DD");
    if (pathTab === "team") {
      if (todayStr < from) from = todayStr;
      if (todayStr > to) to = todayStr;
    }
    const uid = resolveAnalyticsUserId();
    if (!uid) return;
    dispatch(getAttendanceDailyAnalyticsAPI({ from, to, userId: uid }));
  }, [pathTab, overviewMonth, user?.id, selectedEmployee, canReadAllEmployees, canOverview, canTeam]);

  useEffect(() => {
    if (!canHistory || !user?.id) return;
    if (pathTab !== "history") return;
    const uid = resolveAnalyticsUserId();
    if (!uid) return;
    const historyRollupAll =
      canReadAllEmployees && (selectedEmployee === "all" || selectedEmployee == null);
    dispatch(
      getAttendanceDailyAnalyticsAPI({
        from: historyFrom,
        to: historyTo,
        userId: uid,
        view: "history",
        scope: historyRollupAll ? "all" : undefined,
      })
    );
  }, [pathTab, historyFrom, historyTo, user?.id, selectedEmployee, canReadAllEmployees, canHistory]);

  const fetchCalendar = (date = currentDate, employee = employeeSelectValue) => {
    const startDate = moment(date).startOf("month").format("YYYY-MM-DD");
    const endDate = moment(date).endOf("month").format("YYYY-MM-DD");
    dispatch(
      getAttendanceCalendarAPI({
        startDate,
        endDate,
        userId: employee || "me",
      })
    );
  };

  const fetchMachineLogs = (date = currentDate, page = logsPage, explicitRange = null, logsUserOverride) => {
    if (!canMachineLogs) return;
    const defaultStartDate = moment(date).startOf("month").format("YYYY-MM-DD");
    const defaultEndDate = moment(date).endOf("month").format("YYYY-MM-DD");
    const rangeStart = explicitRange?.from || (useWideLogDateRange ? adminDateRange.from : nonAdminDateRange.from);
    const rangeEnd = explicitRange?.to || (useWideLogDateRange ? adminDateRange.to : nonAdminDateRange.to);
    const startDate = rangeStart || defaultStartDate;
    const endDate = rangeEnd || defaultEndDate;
    dispatch(
      getAttendanceLogsAPI({
        startDate,
        endDate,
        userId: resolveLogsApiUserId(logsUserOverride),
        employeeCode: logsFilters.employeeCode || undefined,
        source: logsFilters.source || undefined,
        page,
        limit: 50,
      })
    );
  };

  const fetchMachineUsers = () => {
    if (!canMachineUsersRead || !machineUsersConfigured) return;
    dispatch(getMachineUsersAPI());
  };

  useEffect(() => {
    if (!canOverview || pathTab !== "integration") return;
    fetchCalendar();
  }, [pathTab, canOverview]);

  useEffect(() => {
    if (!user) return;
    if (pathTab !== "integration") return;
    if (canIntegrationConfigRead) {
      dispatch(getAttendanceConfigAPI());
      dispatch(getAttendanceWebhookStatusAPI());
    }
  }, [user, pathTab, canIntegrationConfigRead]);

  useEffect(() => {
    if (!user || !canMachineLogs) return;
    if (pathTab !== "integration") return;
    if (canReadAllEmployees && selectedEmployee == null) return;
    fetchMachineLogs(currentDate, 1);
  }, [user, canMachineLogs, canReadAllEmployees, selectedEmployee, pathTab]);

  useEffect(() => {
    if (!user) return;
    if (pathTab !== "integration") return;
    if (canMachineUsersRead && machineUsersConfigured) {
      fetchMachineUsers();
    }
  }, [user, canMachineUsersRead, machineUsersConfigured, pathTab]);

  useEffect(() => {
    if (config) {
      setConfigForm({
        baseUrl: config.baseUrl || "",
        attendancePath: config.attendancePath || "/iclock/cdata.aspx",
        apiKey: "",
        bearerToken: "",
        siteId: config.siteId || "",
        timeoutMs: config.timeoutMs || 10000,
        webhookSecret: "",
        integrationMode: config.integrationMode || "ADMS_PUSH",
        pullEnabled: Boolean(config.pullEnabled),
        machineUsersListPath: config.machineUsersListPath || "",
        machineUsersCreatePath: config.machineUsersCreatePath || "",
        machineUsersUpdatePath: config.machineUsersUpdatePath || "",
        machineUsersDeletePath: config.machineUsersDeletePath || "",
      });
    }
  }, [config]);

  useEffect(() => {
    if (!lastSyncAt) return;
    if (pathTab !== "integration") return;
    if (canOverview) {
      fetchCalendar();
    }
    fetchMachineLogs(currentDate, 1);
    if (canMachineUsersRead && machineUsersConfigured) fetchMachineUsers();
  }, [lastSyncAt, canOverview, pathTab, canMachineUsersRead, machineUsersConfigured]);

  const events = useMemo(() => {
    const rows = [];
    Object.entries(calendar || {}).forEach(([date, users]) => {
      rows.push({
        id: date,
        title: `${users.length} employee${users.length > 1 ? "s" : ""}`,
        start: new Date(`${date}T00:00:00`),
        end: new Date(`${date}T00:00:00`),
        allDay: true,
        resource: users,
      });
    });
    return rows;
  }, [calendar]);

  const onSelectEvent = (event) => {
    const details = (event.resource || []).flatMap((u) =>
      (u.punches || []).map((p) => ({
        employee: `${u.firstname || ""} ${u.lastname || ""}`.trim() || u.employeeCode,
        employeeCode: u.employeeCode,
        punchType: p.punchType,
        punchTime: p.punchTime,
      }))
    );
    setSelectedDayRows(details.sort((a, b) => new Date(a.punchTime) - new Date(b.punchTime)));
  };

  const onSelectEmployee = (value) => {
    setSelectedEmployee(value);
    if (canOverview) {
      fetchCalendar(currentDate, value);
    }
    if (canMachineLogs) {
      setLogsPage(1);
      fetchMachineLogs(currentDate, 1, null, value);
    }
  };

  const handleSync = () => {
    const startDate = moment(currentDate).startOf("month").format("YYYY-MM-DD");
    const endDate = moment(currentDate).endOf("month").format("YYYY-MM-DD");
    dispatch(syncAttendanceAPI({ startDate, endDate, force: true })).then(() => {
      dispatch(getAttendanceWebhookStatusAPI());
    });
  };

  const handleSaveConfig = () => {
    dispatch(updateAttendanceConfigAPI(configForm));
  };

  const onApplyLogFilters = () => {
    setLogsPage(1);
    fetchMachineLogs(currentDate, 1);
  };

  const onApplyNonAdminDateRange = () => {
    setLogsPage(1);
    fetchMachineLogs(currentDate, 1, nonAdminDateRange);
  };

  const onSetTodayRange = () => {
    const today = moment().format("YYYY-MM-DD");
    const nextRange = { from: today, to: today };
    if (useWideLogDateRange) setAdminDateRange(nextRange);
    else setNonAdminDateRange(nextRange);
    setLogsPage(1);
    fetchMachineLogs(currentDate, 1, nextRange);
  };

  const onSetLast7DaysRange = () => {
    const nextRange = {
      from: moment().subtract(6, "days").format("YYYY-MM-DD"),
      to: moment().format("YYYY-MM-DD"),
    };
    if (useWideLogDateRange) setAdminDateRange(nextRange);
    else setNonAdminDateRange(nextRange);
    setLogsPage(1);
    fetchMachineLogs(currentDate, 1, nextRange);
  };

  const onSetThisMonthRange = () => {
    const nextRange = {
      from: moment().startOf("month").format("YYYY-MM-DD"),
      to: moment().endOf("month").format("YYYY-MM-DD"),
    };
    if (useWideLogDateRange) setAdminDateRange(nextRange);
    else setNonAdminDateRange(nextRange);
    setLogsPage(1);
    fetchMachineLogs(currentDate, 1, nextRange);
  };

  const onLogPageChange = (nextPage) => {
    setLogsPage(nextPage);
    fetchMachineLogs(currentDate, nextPage);
  };

  const resetMachineUserForm = () => {
    setMachineUserForm({
      employeeCode: "",
      name: "",
      password: "",
      role: "employee",
      cardNo: "",
    });
  };

  const onCreateMachineUser = () => {
    dispatch(createMachineUserAPI(machineUserForm)).then(() => {
      fetchMachineUsers();
      resetMachineUserForm();
    });
  };

  const onUpdateMachineUser = () => {
    if (!machineUserForm.employeeCode) return;
    dispatch(
      updateMachineUserAPI({
        employeeCode: machineUserForm.employeeCode,
        payload: machineUserForm,
      })
    ).then(() => {
      fetchMachineUsers();
    });
  };

  const onDeleteMachineUser = (employeeCode) => {
    dispatch(deleteMachineUserAPI(employeeCode)).then(() => {
      fetchMachineUsers();
    });
  };

  const onSyncDashboardUser = () => {
    if (!selectedDashboardUserId) return;
    dispatch(syncDashboardUserToMachineAPI(selectedDashboardUserId)).then(() => {
      fetchMachineUsers();
    });
  };

  const showTeamAttendancePanel = canTeam && (pathTab === "overview" || pathTab === "team");
  const showPersonalOverviewPanel =
    canOverview && !canTeam && (pathTab === "overview" || pathTab === "team");

  return (
    <div className="attendance-page">
      <div className="attendance-page-header mb-4">
        <h1 className="page-title">Attendance Tracking</h1>
        <p className="page-subtitle">Track employee attendance accurately and manage time effortlessly.</p>
      </div>

      <div className="attendance-tabs-row card-border card-body mb-4">
        <NavLink to="/attendance" end className={({ isActive }) => `attendance-tab${isActive ? " attendance-tab--active" : ""}`}>
          Overview
        </NavLink>

        {canOverview && canTeam ? (
          <NavLink
            to="/attendance/team"
            className={({ isActive }) => `attendance-tab${isActive ? " attendance-tab--active" : ""}`}
          >
            Team
          </NavLink>
        ) : null}
        <NavLink
          to="/attendance/history"
          className={({ isActive }) => `attendance-tab${isActive ? " attendance-tab--active" : ""}`}
        >
          History
        </NavLink>
        {canIntegration ? (
          <NavLink
            to="/attendance/integration"
            className={({ isActive }) => `attendance-tab${isActive ? " attendance-tab--active" : ""}`}
          >
            Integration
          </NavLink>
        ) : null}
      </div>

      {(showPersonalOverviewPanel || pathTab === "history") && canReadAllEmployees ? (
        <PageContainerHeader
          title={pathTab === "history" ? "Attendance history" : "Attendance"}
          selection={canReadAllEmployees}
          selectValue={employeeSelectValue}
          employeeSelectExtraOptions={attendanceEmployeeExtraOptions}
          employeeSelectExcludeCeoCmo
          employeeSelectIncludeExEmployees
          onSelectEmployee={onSelectEmployee}
        />
      ) : null}

      {showTeamAttendancePanel ? <AttendanceTeamPanel /> : null}

      {showPersonalOverviewPanel ? (
        <AttendanceOverviewPanel
          monthMoment={overviewMonth}
          onPrevMonth={() => setOverviewMonth((m) => m.clone().subtract(1, "month"))}
          onNextMonth={() => setOverviewMonth((m) => m.clone().add(1, "month"))}
          analyticsDaily={analyticsOverview}
          analyticsLoading={analyticsLoading}
          shift={analyticsOverview?.shift}
          showStatCards={isAdminRole || canReadAllEmployees}
          statCardMode={pathTab === "team" && (isAdminRole || canReadAllEmployees) ? "today" : "average"}
        />
      ) : null}

      {pathTab === "history" && canHistory ? (
        <>
          <div className="card-border card-body mb-4 attendance-history-filters flex flex-wrap gap-3 items-end">
            <input
              type="date"
              className="search-input"
              value={historyFrom}
              onChange={(e) => setHistoryFrom(e.target.value)}
            />
            <input
              type="date"
              className="search-input"
              value={historyTo}
              onChange={(e) => setHistoryTo(e.target.value)}
            />
            <span className="text-xs text-[#6f7d97] pb-2">Summary range (punch-based)</span>
          </div>
          <AttendanceHistoryPanel
            analyticsDaily={analyticsHistory}
            analyticsLoading={analyticsLoading}
            historySearch={historySearch}
            onSearchChange={setHistorySearch}
          />
        </>
      ) : null}

      {pathTab === "integration" && canIntegration ? (
        <>
          {canIntegrationMutate && <div className="attendance-actions card-border card-body mb-4">
            <button className="button bg-btn-primary text-white px-4 py-2" onClick={handleSync} disabled={syncing}>
              {syncing ? "Syncing..." : "Manual Re-import Logs"}
            </button>
            <div className="text-xs text-[#6f7d97]">
              Use manual sync for backfill/recovery windows.
            </div>
          </div>}

          {canIntegrationMutate && (
            <div className="attendance-config card-border card-body mb-4">
              <h4 className="mb-4 text-black">e-SSL Integration Config</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <select
                  className="search-input"
                  value={configForm.integrationMode}
                  onChange={(e) => setConfigForm((s) => ({ ...s, integrationMode: e.target.value }))}
                >
                  <option value="ADMS_PUSH">ADMS Push (recommended)</option>
                  <option value="API_PULL">API Pull</option>
                  <option value="HYBRID">Hybrid</option>
                </select>
                <label className="flex items-center gap-2 text-sm text-[#5f6d8a]">
                  <input
                    type="checkbox"
                    checked={Boolean(configForm.pullEnabled)}
                    onChange={(e) => setConfigForm((s) => ({ ...s, pullEnabled: e.target.checked }))}
                  />
                  Enable API pull sync
                </label>
                <input
                  className="search-input"
                  placeholder="API Base URL"
                  value={configForm.baseUrl}
                  onChange={(e) => setConfigForm((s) => ({ ...s, baseUrl: e.target.value }))}
                />
                <input
                  className="search-input"
                  placeholder="Attendance API path"
                  value={configForm.attendancePath}
                  onChange={(e) => setConfigForm((s) => ({ ...s, attendancePath: e.target.value }))}
                />
                <input
                  className="search-input"
                  placeholder="API key (optional)"
                  value={configForm.apiKey}
                  onChange={(e) => setConfigForm((s) => ({ ...s, apiKey: e.target.value }))}
                />
                <input
                  className="search-input"
                  placeholder="Bearer token (optional)"
                  value={configForm.bearerToken}
                  onChange={(e) => setConfigForm((s) => ({ ...s, bearerToken: e.target.value }))}
                />
                <input
                  className="search-input"
                  placeholder="Site ID (optional)"
                  value={configForm.siteId}
                  onChange={(e) => setConfigForm((s) => ({ ...s, siteId: e.target.value }))}
                />
                <input
                  className="search-input"
                  type="number"
                  placeholder="Timeout (ms)"
                  value={configForm.timeoutMs}
                  onChange={(e) => setConfigForm((s) => ({ ...s, timeoutMs: Number(e.target.value) }))}
                />
                <input
                  className="search-input md:col-span-2"
                  placeholder="Webhook secret (optional)"
                  value={configForm.webhookSecret}
                  onChange={(e) => setConfigForm((s) => ({ ...s, webhookSecret: e.target.value }))}
                />
                <input
                  className="search-input"
                  placeholder="Machine users list path (optional for F22)"
                  value={configForm.machineUsersListPath}
                  onChange={(e) => setConfigForm((s) => ({ ...s, machineUsersListPath: e.target.value }))}
                />
                <input
                  className="search-input"
                  placeholder="Machine users create path (optional for F22)"
                  value={configForm.machineUsersCreatePath}
                  onChange={(e) => setConfigForm((s) => ({ ...s, machineUsersCreatePath: e.target.value }))}
                />
                <input
                  className="search-input"
                  placeholder="Machine users update path (optional for F22)"
                  value={configForm.machineUsersUpdatePath}
                  onChange={(e) => setConfigForm((s) => ({ ...s, machineUsersUpdatePath: e.target.value }))}
                />
                <input
                  className="search-input"
                  placeholder="Machine users delete path (optional for F22)"
                  value={configForm.machineUsersDeletePath}
                  onChange={(e) => setConfigForm((s) => ({ ...s, machineUsersDeletePath: e.target.value }))}
                />
              </div>
              <div className="mt-4">
                <button className="button bg-btn-primary text-white px-4 py-2" onClick={handleSaveConfig} disabled={savingConfig}>
                  {savingConfig ? "Saving..." : "Save Configuration"}
                </button>
              </div>
            </div>
          )}

          {canIntegrationConfigRead && (
            <div className="card-border card-body mb-4">
              <h4 className="mb-3">Webhook Health</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="attendance-health-card">
                  <div className="attendance-health-label">Last Webhook</div>
                  <div className="attendance-health-value">
                    {webhookStatus?.lastWebhookAt ? moment(webhookStatus.lastWebhookAt).format("DD MMM YYYY, hh:mm A") : "No webhook yet"}
                  </div>
                </div>
                <div className="attendance-health-card">
                  <div className="attendance-health-label">Source IP</div>
                  <div className="attendance-health-value">{webhookStatus?.sourceIp || "-"}</div>
                </div>
                <div className="attendance-health-card">
                  <div className="attendance-health-label">Last Device ID</div>
                  <div className="attendance-health-value">{webhookStatus?.lastDeviceId || "-"}</div>
                </div>
                <div className="attendance-health-card">
                  <div className="attendance-health-label">Webhook Punches</div>
                  <div className="attendance-health-value">{webhookStatus?.totalWebhookPunches || 0}</div>
                </div>
              </div>
            </div>
          )}

          {canOverview && <div className="card-border card-body attendance-calendar-card">
            {loading ? (
              <div>Loading attendance...</div>
            ) : (
              <div className="attendance-calendar-wrap">
                <Calendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: "100%", minHeight: "inherit" }}
                  views={["month"]}
                  date={currentDate}
                  onNavigate={(date) => {
                    setCurrentDate(date);
                    fetchCalendar(date, employeeSelectValue);
                    fetchMachineLogs(date, 1);
                    setLogsPage(1);
                  }}
                  onSelectEvent={onSelectEvent}
                  eventPropGetter={() => ({
                    style: {
                      background: "rgba(116, 77, 255, 0.85)",
                      border: "1px solid rgba(167, 139, 250, 0.85)",
                      color: "#fff",
                      borderRadius: "8px",
                      textAlign: "center",
                    },
                  })}
                />
              </div>
            )}
          </div>}

          {canOverview && <div className="attendance-timeline card-border card-body mt-4">
            <h4 className="mb-4">Punch Timeline ({timeZoneLabel})</h4>
            {!selectedDayRows.length ? (
              <div className="text-sm">Select a date on calendar to view punch logs.</div>
            ) : (
              <div className="flex flex-col gap-3">
                {selectedDayRows.map((row, idx) => {
                  const dayPunchMeta = classifyPunchForDisplay(row.punchTime);
                  return (
                    <div key={`${row.employeeCode}-${row.punchTime}-${idx}`} className="attendance-row">
                      <div>
                        <div className="attendance-employee">{row.employee}</div>
                        <div className="attendance-code">{row.employeeCode}</div>
                      </div>
                      <div className="attendance-type">{row.punchType}</div>
                      <div className="attendance-row-punch-meta">
                        <span className="attendance-punch-type">{dayPunchMeta.label}</span>
                        {dayPunchMeta.isLate ? (
                          <span className="attendance-punch-late-badge">Late</span>
                        ) : null}
                        <span className="attendance-row-time">{formatPunchDisplay(row.punchTime)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>}

          {canMachineLogs && (
            <div className="attendance-log-view card-border card-body mt-4">
              <div className="attendance-log-header">
                <h4>
                  {canReadAllEmployees
                    ? selectedEmployee === "all" || selectedEmployee == null
                      ? "Machine Logs (All Employees)"
                      : "Machine Logs (Selected Employee)"
                    : "My Attendance Logs"}
                </h4>
                {canReadAllEmployees && <div className="attendance-log-filters">
                  <input
                    className="search-input"
                    placeholder="Filter by employee code"
                    value={logsFilters.employeeCode}
                    onChange={(e) => setLogsFilters((s) => ({ ...s, employeeCode: e.target.value }))}
                  />
                  <select
                    className="search-input"
                    value={logsFilters.source}
                    onChange={(e) => setLogsFilters((s) => ({ ...s, source: e.target.value }))}
                  >
                    <option value="">All sources</option>
                    <option value="ESSL_WEBHOOK">Webhook</option>
                    <option value="ESSL_API">API Pull</option>
                  </select>
                  <button className="button bg-btn-primary text-white px-4 py-2" onClick={onApplyLogFilters}>
                    Apply
                  </button>
                </div>}
              </div>
              <div className="attendance-log-filters mb-3">
                <input
                  className="search-input"
                  type="date"
                  value={useWideLogDateRange ? adminDateRange.from : nonAdminDateRange.from}
                  onChange={(e) =>
                    useWideLogDateRange
                      ? setAdminDateRange((s) => ({ ...s, from: e.target.value }))
                      : setNonAdminDateRange((s) => ({ ...s, from: e.target.value }))
                  }
                />
                <input
                  className="search-input"
                  type="date"
                  value={useWideLogDateRange ? adminDateRange.to : nonAdminDateRange.to}
                  onChange={(e) =>
                    useWideLogDateRange
                      ? setAdminDateRange((s) => ({ ...s, to: e.target.value }))
                      : setNonAdminDateRange((s) => ({ ...s, to: e.target.value }))
                  }
                />
                <button className="button px-4 py-2" onClick={onSetTodayRange}>
                  Today
                </button>
                <button className="button px-4 py-2" onClick={onSetLast7DaysRange}>
                  Last 7 Days
                </button>
                <button className="button px-4 py-2" onClick={onSetThisMonthRange}>
                  This Month
                </button>
                <button className="button bg-btn-primary text-white px-4 py-2" onClick={onApplyNonAdminDateRange}>
                  Apply
                </button>
              </div>
              <div className="attendance-timezone-note">Time Zone: {timeZoneLabel}</div>

              {logsLoading ? (
                <div className="text-sm">Loading machine logs...</div>
              ) : !logs.length ? (
                <div className="text-sm">No logs found for the selected range.</div>
              ) : (
                <div className="attendance-log-table-wrap">
                  <table
                    className={`attendance-log-table${showStaffHistoryView ? " attendance-log-table--staff" : " attendance-log-table--employee"}`}
                  >
                    <thead>
                      <tr>
                        {showStaffHistoryView ? (
                          <>
                            <th>Employee (Machine)</th>
                            <th>Employee Code</th>
                            <th>In / Out time</th>
                            <th>Source</th>
                            {showIntegrationDetailColumns ? (
                              <>
                                <th>Punch Type</th>
                                <th>Device Log ID</th>
                              </>
                            ) : null}
                          </>
                        ) : (
                          <th>In / Out time</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {logsSorted.map((row) => {
                        const punchMeta = classifyPunchForDisplay(row.punchTime);
                        const employeeLabel =
                          row.machineEmployeeName || `${row.firstname || ""} ${row.lastname || ""}`.trim() || row.employeeCode || "-";

                        if (showStaffHistoryView) {
                          return (
                            <tr key={row.id}>
                              <td>{employeeLabel}</td>
                              <td>{row.employeeCode}</td>
                              <td>
                                <div className="attendance-punch-cell">
                                  <span className="attendance-punch-type">{punchMeta.label}</span>
                                  <span className="attendance-punch-value">{formatPunchDisplay(row.punchTime)}</span>
                                  {punchMeta.isLate ? (
                                    <span className="attendance-punch-late-badge">Late</span>
                                  ) : null}
                                </div>
                              </td>
                              <td>{row.source || "-"}</td>
                              {showIntegrationDetailColumns ? (
                                <>
                                  <td>{row.punchType}</td>
                                  <td>{row.deviceLogId || "-"}</td>
                                </>
                              ) : null}
                            </tr>
                          );
                        }

                        return (
                          <tr key={row.id}>
                            <td>
                              <div className="attendance-punch-cell">
                                <span className="attendance-punch-type">{punchMeta.label}</span>
                                <span className="attendance-punch-value">{formatPunchDisplay(row.punchTime)}</span>
                                {punchMeta.isLate ? (
                                  <span className="attendance-punch-late-badge">Late</span>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="attendance-log-pagination">
                <button
                  className="button px-3 py-1"
                  disabled={logsPagination.page <= 1 || logsLoading}
                  onClick={() => onLogPageChange(logsPagination.page - 1)}
                >
                  Prev
                </button>
                <div className="text-sm">
                  Page {logsPagination.page} | Total {logsPagination.total}
                </div>
                <button
                  className="button px-3 py-1"
                  disabled={logsPagination.page * logsPagination.limit >= logsPagination.total || logsLoading}
                  onClick={() => onLogPageChange(logsPagination.page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {canMachineUsersRead && canReadAllEmployees && machineUsersConfigured && (
            <div className="attendance-machine-users card-border card-body mt-4">
              <div className="attendance-log-header">
                <h4>Machine User Management</h4>
                <button className="button px-4 py-2" onClick={fetchMachineUsers} disabled={machineUsersLoading}>
                  {machineUsersLoading ? "Refreshing..." : "Refresh Machine Users"}
                </button>
              </div>

              <div className="attendance-machine-grid">
                <input
                  className="search-input"
                  placeholder="Employee Code"
                  value={machineUserForm.employeeCode}
                  onChange={(e) => setMachineUserForm((s) => ({ ...s, employeeCode: e.target.value }))}
                  disabled={!canIntegrationMutate}
                />
                <input
                  className="search-input"
                  placeholder="Name"
                  value={machineUserForm.name}
                  onChange={(e) => setMachineUserForm((s) => ({ ...s, name: e.target.value }))}
                  disabled={!canIntegrationMutate}
                />
                <input
                  className="search-input"
                  placeholder="Password (optional)"
                  value={machineUserForm.password}
                  onChange={(e) => setMachineUserForm((s) => ({ ...s, password: e.target.value }))}
                  disabled={!canIntegrationMutate}
                />
                <input
                  className="search-input"
                  placeholder="Role (e.g. employee/admin)"
                  value={machineUserForm.role}
                  onChange={(e) => setMachineUserForm((s) => ({ ...s, role: e.target.value }))}
                  disabled={!canIntegrationMutate}
                />
                <input
                  className="search-input"
                  placeholder="Card No (optional)"
                  value={machineUserForm.cardNo}
                  onChange={(e) => setMachineUserForm((s) => ({ ...s, cardNo: e.target.value }))}
                  disabled={!canIntegrationMutate}
                />
                <div className="attendance-machine-actions">
                  <button className="button bg-btn-primary text-white px-4 py-2" onClick={onCreateMachineUser} disabled={machineUsersMutating || !canIntegrationMutate}>
                    Create on Machine
                  </button>
                  <button className="button px-4 py-2" onClick={onUpdateMachineUser} disabled={machineUsersMutating || !canIntegrationMutate}>
                    Update on Machine
                  </button>
                </div>
              </div>

              <div className="attendance-machine-sync mt-4">
                <div className="text-sm">Sync user from dashboard to machine</div>
                <div className="attendance-machine-sync-row">
                  <EmployeeSelect
                    value={selectedDashboardUserId}
                    onSelectEmployee={(val) => setSelectedDashboardUserId(val)}
                    width="340px"
                    placeholder="Select dashboard employee..."
                    disabled={!canIntegrationMutate}
                  />
                  <button className="button bg-btn-primary text-white px-4 py-2" onClick={onSyncDashboardUser} disabled={machineUsersMutating || !canIntegrationMutate}>
                    Sync Selected Employee
                  </button>
                </div>
              </div>

              <div className="attendance-log-table-wrap mt-4">
                <table className="attendance-log-table">
                  <thead>
                    <tr>
                      <th>Employee Code</th>
                      <th>Machine Name</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!machineUsers.length ? (
                      <tr>
                        <td colSpan={3}>No machine users found.</td>
                      </tr>
                    ) : machineUsers.map((row, idx) => (
                      <tr key={`${row.employeeCode}-${idx}`}>
                        <td>{row.employeeCode}</td>
                        <td>{row.machineEmployeeName || "-"}</td>
                        <td>
                          <div className="attendance-machine-actions">
                            <button
                              className="button px-3 py-1"
                              onClick={() =>
                                setMachineUserForm({
                                  employeeCode: row.employeeCode,
                                  name: row.machineEmployeeName || "",
                                  password: "",
                                  role: "employee",
                                  cardNo: "",
                                })
                              }
                              disabled={!canIntegrationMutate}
                            >
                              Edit
                            </button>
                            <button
                              className="button px-3 py-1"
                              onClick={() => onDeleteMachineUser(row.employeeCode)}
                              disabled={machineUsersMutating || !canIntegrationMutate}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default AttendancePage;
